import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  InfoCircleFilled,
} from "@ant-design/icons";

import { Avatar, Button, Empty, Flex, Input, List, Modal } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  useCreateScanToOrder,
  useAppleValidateMerchant,
  useValidateCheckoutReadyForScanOrder,
} from "../../api/api.post";
import { useNotifications2 } from "../../components/notifications/notification";
import { useStoreContext } from "../../providers/store.provider";
import {
  IProductForScanOrder,
  IProductOnline,
  ScanToOrderPayment,
  StoreDetailsPublicResponse,
} from "../../types/types";
import {
  EXHUT_BIZ_SCAN_TO_ORDER_TABLE_CODE,
  EXHUT_DOH_SCAN_ORDER_AUTH_CODE,
} from "../../utils/constants";
import { capitalize, formatAppleTokenToFiserv } from "../../utils/functions";
import { calculateProductTotal } from "../../utils/product.utils";
import { toFixed, toFixedDisplay } from "../../utils/number.utils";
import { PlainLottie } from "../../components/lottie.viewer.component";
import * as greenCheckJson from "../../animations/green-check.json";

export default function StoCheckout({
  open,
  onCancel,
  onOK,
  storeDetails,
  items,
  onRemoveItems,
}: {
  storeDetails?: StoreDetailsPublicResponse;
  items: IProductOnline[];
  open: boolean;
  onOK: () => void;
  onCancel: () => void;
  onRemoveItems: (item: IProductOnline) => void;
}) {
  const { id } = useParams();
  const [newLoginCode, setNewLoginCode] = useState<string>();

  const grossTotal = useMemo(
    () => calculateProductTotal(items, storeDetails?.tax),
    [items, storeDetails],
  );

  const { mutateAsync, isPending } = useCreateScanToOrder(id);
  const {
    mutateAsync: validateCheckoutReadyAsync,
    isPending: validateCheckoutReadyPending,
  } = useValidateCheckoutReadyForScanOrder(id);

  const tableCode = localStorage.getItem(EXHUT_BIZ_SCAN_TO_ORDER_TABLE_CODE);

  const [promptNewLoginCode, setPromptNewLoginCode] = useState<boolean>(false);

  const { notifyError, notifySuccess, notification } = useNotifications2();

  const { mutateAsync: validateAppleMerchantAsync } =
    useAppleValidateMerchant(id);

  const [itemsToCheckout, setItemsToCheckout] = useState<
    IProductForScanOrder[]
  >([]);

  const [error, setError] = useState<string>();

  // Shows the success (green check) view inside the modal after a successful
  // order placement, until the user taps OK.
  const [orderPlaced, setOrderPlaced] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setPromptNewLoginCode(false);
      setOrderPlaced(false);
      validateCheckoutReadyAsync({
        tableCode: tableCode ? +tableCode : undefined,
      })
        .then(() => setError(undefined))
        .catch((e) => {
          const statusCode = e.response?.data?.statusCode;
          if (statusCode === 400) {
            setError("Login code required.");
            setPromptNewLoginCode(true);
            // notifyError("Unable to find your table.");
          } else if (statusCode === 404) {
            // either store or table is not found.
            const _msg = e.response?.data?.message;
            if (_msg.includes("Table")) {
              setError("Unable to find your table.");
            } else {
              setError("Store Busy. Please Try After Sometime.");
            }
          } else if (statusCode === 401) {
            setError("Login Code Expired.");
            setPromptNewLoginCode(true);
          } else {
            notifyError("Unable to process. Please call your waiter ");
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tableCode]);

  const handleValidateAuthCode = () => {
    if (newLoginCode) {
      localStorage.setItem(EXHUT_DOH_SCAN_ORDER_AUTH_CODE, newLoginCode);
    }
    setPromptNewLoginCode(false);
    setError(undefined);
    validateCheckoutReadyAsync({
      tableCode: tableCode ? +tableCode : undefined,
    })
      .then(() =>
        notifySuccess({ title: "Success", description: "Code Accepted" }),
      )
      .catch((e) => {
        const statusCode = e.response?.data?.statusCode;
        if (statusCode === 400) {
          setError("Unable to find your table.");
        } else if (statusCode === 404) {
          setError("Store Busy. Please try after sometime.");
        } else if (statusCode === 401) {
          setError("Invalid code");
          setPromptNewLoginCode(true);
        } else {
          notifyError("Unable to process. Please call your waiter ");
        }
      });
  };

  useEffect(() => {
    if (items) {
      setItemsToCheckout(
        items.map((item) => {
          return {
            ...item,
            uniqueId: uuidv4(),
          };
        }),
      );
    }
  }, [items]);

  const handleSubmit = (payment?: ScanToOrderPayment) => {
    if (newLoginCode) {
      localStorage.setItem(EXHUT_DOH_SCAN_ORDER_AUTH_CODE, newLoginCode);
    }
    setPromptNewLoginCode(false);
    mutateAsync({
      products: itemsToCheckout,
      tableCode: tableCode ? +tableCode : 0,
      payment,
    })
      .then(() => {
        setOrderPlaced(true);
      })
      .catch((err) => {
        setError("Please try again");
        setPromptNewLoginCode(true);
        if (err.status === 401) {
          notifyError("Login code expired. Please enter new one.");
        } else if (err.status === 403) {
          notifyError("Please enter new login.");
        } else {
          notifyError("Whoops! Something went wrong.");
        }
      });
  };

  const handleNotesChange = (
    product: IProductForScanOrder,
    notesText: string,
  ) => {
    const _items = [...itemsToCheckout];
    const _item = _items.find((item) => item.uniqueId === product.uniqueId);
    const newItem: IProductForScanOrder = { ..._item!, notes: notesText };
    _items.splice(0, 1, newItem);
    setItemsToCheckout(_items);
  };

  const handleApplePay = async () => {
    console.log("Handle apple pay start1");
    if (!window.PaymentRequest) {
      alert("Apple Pay not supported in this browser");
      return;
    }

    const supportedInstruments: PaymentMethodData[] = [
      {
        supportedMethods: "https://apple.com/apple-pay",
        data: {
          version: 3,
          merchantIdentifier: "merchant.expensehut.com",
          merchantCapabilities: ["supports3DS"],
          supportedNetworks: ["visa", "masterCard", "amex", "discover"],
          countryCode: "US",
          currencyCode: "USD",
        },
      },
    ];

    const details: PaymentDetailsInit = {
      total: {
        label: "ExpenseHut",
        amount: { currency: "USD", value: toFixedDisplay(grossTotal) },
      },
    };

    try {
      console.log("Start payment request");
      const request = new PaymentRequest(supportedInstruments, details);

      request.addEventListener("merchantvalidation", (event: any) => {
        console.log("Merchant validation start", event);
        validateAppleMerchantAsync({ validationUrl: "" })
          .then((res) => {
            event.complete(res);
          })
          .catch(() => {
            notifyError("Whoops! Let's try again.");
          });
      });

      // Show Apple Pay sheet
      const paymentResponse = await request.show();

      console.log("Done paymentsheet");
      // You would send this to your backend to tokenize with Fiserv/CardPointe
      console.log("Apple Pay response:", paymentResponse);
      console.log(
        "Apple Pay parsed value:",
        formatAppleTokenToFiserv({
          paymentData: paymentResponse,
          type: "APPLE",
        }),
      );

      // Mark success
      await paymentResponse.complete("success");
      console.log("Payment is full simulated");
      handleSubmit({
        paymentData: paymentResponse,
        type: "APPLE",
        amount: toFixed(grossTotal),
      });
    } catch (err: any) {
      if (err?.name === "AbortError") {
        // do nothing. User has cancelled the payment
      } else {
        console.error("Apple Pay Payment failed", err);
        notifyError(err.message ?? "Payment Failed!");
      }
    }
  };
  const errorBanner = !!error && (
    <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 shadow-sm">
      <ExclamationCircleFilled
        style={{ color: "#ef4444", fontSize: 20, flexShrink: 0 }}
      />
      <span className="text-red-600 font-semibold text-sm">{error}</span>
    </div>
  );

  return (
    <Modal
      title="Checkout"
      open={open}
      onCancel={onCancel}
      footer={null}
      closable={false}
    >
      {notification}

      {orderPlaced && (
        <div className="my-4 flex flex-col items-center gap-6">
          <div className="w-full rounded-md border border-green-500 bg-green-50 p-6">
            <div className="flex flex-col items-center gap-2">
              <PlainLottie animationData={greenCheckJson} size={110} />
              <strong className="text-xl text-green-700">
                Order Successfully Placed!
              </strong>
              <span className="text-center text-sm text-green-700">
                Your order has been sent to the kitchen.
              </span>
            </div>
          </div>

          <div className="w-full rounded-xl border border-blue-300 bg-blue-50 px-4 py-3 flex items-start gap-3 shadow-sm">
            <InfoCircleFilled
              style={{ color: "#2563eb", fontSize: 22, marginTop: 2 }}
            />
            <p className="text-blue-900 font-semibold text-base leading-snug">
              Hit <span className="font-extrabold">OK</span> and track your
              order status anytime from the{" "}
              <span className="font-extrabold">"View Order"</span> button.
            </p>
          </div>

          <Button type="primary" className="w-full" onClick={onOK}>
            OK
          </Button>
        </div>
      )}

      {!orderPlaced && (
        <div className="divColumn">
          <div className="my-10">
            {itemsToCheckout.length === 0 && <Empty description="No Items" />}
            {itemsToCheckout.length > 0 && (
              <Flex vertical gap={10}>
                <List
                  dataSource={itemsToCheckout}
                  itemLayout="horizontal"
                  bordered
                  renderItem={(item, index) => (
                    <RowItem
                      item={item}
                      key={index}
                      onRemoveItems={(item) => onRemoveItems(item)}
                      onNotesChange={(
                        item: IProductForScanOrder,
                        notes: string,
                      ) => handleNotesChange(item, notes)}
                    />
                  )}
                />
                {/* Error shown on its own when we are not prompting for a new code */}
                {!promptNewLoginCode && errorBanner}
                {promptNewLoginCode && (
                  <Flex className="mt-10" gap={10} vertical>
                    <Input
                      placeholder="New Login Code.."
                      value={newLoginCode}
                      maxLength={6}
                      onChange={(e) => setNewLoginCode(e.target.value)}
                    />
                    {errorBanner}
                    <Button
                      loading={validateCheckoutReadyPending}
                      type="primary"
                      size="large"
                      onClick={handleValidateAuthCode}
                    >
                      Validate Code
                    </Button>
                  </Flex>
                )}

                {!error && (
                  <>
                    <div className="flex items-center justify-center gap-2 rounded-xl border border-green-200 bg-green-50 py-2.5 mb-3 text-green-700 font-semibold shadow-sm">
                      <CheckCircleFilled style={{ color: "#22c55e" }} />
                      Ready To Send.
                    </div>
                    {storeDetails?.requirePaymentForSto && (
                      <button
                        style={{
                          backgroundColor: "black",
                          color: "white",
                          fontSize: "18px",
                          padding: "12px 20px",
                          borderRadius: "8px",
                        }}
                        onClick={handleApplePay}
                      >
                        Pay with  Apple Pay
                      </button>
                    )}

                    {!storeDetails?.requirePaymentForSto && (
                      <Button
                        type="primary"
                        className="w-full"
                        size="large"
                        disabled={itemsToCheckout.length === 0}
                        onClick={() => handleSubmit()}
                        loading={isPending}
                      >
                        {"Place Order"}
                      </Button>
                    )}
                  </>
                )}
              </Flex>
            )}
          </div>

          <Button
            block
            icon={<ArrowLeftOutlined />}
            onClick={onCancel}
            style={{ height: 60, fontSize: 20, fontWeight: 700 }}
          >
            Go back
          </Button>
        </div>
      )}
    </Modal>
  );
}

function RowItem({
  item,
  onRemoveItems,
  onNotesChange,
}: {
  item: IProductForScanOrder;
  onRemoveItems: (item: IProductForScanOrder) => void;
  onNotesChange: (item: IProductForScanOrder, notes: string) => void;
}) {
  const { currency } = useStoreContext();

  return (
    <List.Item style={{ padding: "14px 16px" }}>
      <Flex vertical style={{ flex: 1, gap: 10 }}>
        <Flex align="center" gap={12} style={{ flex: 1 }}>
          <Avatar
            shape="square"
            size={56}
            src={item.cover}
            style={{ borderRadius: 12, flexShrink: 0 }}
          />
          <div className="flex flex-1 flex-col min-w-0">
            <span className="text-base font-semibold leading-tight truncate">
              {capitalize(item.title)}
            </span>
            <span className="text-sm font-bold text-indigo-600 mt-0.5">
              {currency}
              {toFixedDisplay(item.price?.value)}
            </span>
          </div>
          <Button
            type="text"
            shape="circle"
            aria-label="Remove item"
            className="hover:!bg-red-50"
            icon={<DeleteOutlined style={{ color: "#ef4444" }} />}
            onClick={() => onRemoveItems(item)}
          />
        </Flex>

        <Input
          placeholder="Special Instructions..."
          prefix={<EditOutlined style={{ color: "rgba(0,0,0,0.35)" }} />}
          allowClear
          onChange={(e) => onNotesChange(item, e.target.value)}
        />
      </Flex>
    </List.Item>
  );
}
